import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import {
  getCurrentOwner,
  getOwnerShops,
  getActiveShopId,
  setActiveShopId,
  setCurrentShop,
  getCurrentShop,
  setCurrentUser,
  setOwnerShops,
} from "@/lib/auth";
import {
  formatCurrency,
  getCurrencyName,
  getCurrencyDisplayName,
} from "@/lib/currency";
import {
  getOwnerShops as fetchOwnerShops,
  getOwnerRealtimeStats,
} from "@/lib/api";
import { getOwnerSupervisors, createOwnerSupervisor, updateOwnerSupervisor, deleteOwnerSupervisor } from "@/lib/api/users-api";
import {
  Building,
  Store,
  Users,
  Package,
  TrendingUp,
  AlertCircle,
  Plus,
  ArrowRight,
  RefreshCw,
  Loader2,
  DollarSign,
  ShoppingCart,
  BarChart3,
  Activity,
  Crown,
  Pencil,
  Trash2,
} from "lucide-react";
import ShopEditModal from "@/components/ShopEditModal";
import OwnerReports from "@/pages/OwnerReports";
import OwnerDashboardKPI from "@/pages/OwnerDashboardKPI";
import OwnerAccountSettings from "@/components/OwnerAccountSettings";
import { usePlan } from "@/hooks/usePlan";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function OwnerDashboard() {
  const [, setLocation] = useLocation();
  const [activeShop, setActiveShop] = useState<string | null>(null);
  const [editingShop, setEditingShop] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateSupervisorOpen, setIsCreateSupervisorOpen] = useState(false);
  const [supervisorForm, setSupervisorForm] = useState({
    shopId: "",
    username: "",
    password: "",
    fullName: "",
    email: "",
    phone: "",
  });
  const [editingSupervisor, setEditingSupervisor] = useState<any>(null);
  const [isEditSupervisorOpen, setIsEditSupervisorOpen] = useState(false);
  const [editSupervisorForm, setEditSupervisorForm] = useState({
    fullName: "",
    username: "",
    email: "",
    phone: "",
    password: "",
  });
  const [deletingSupervisor, setDeletingSupervisor] = useState<any>(null);
  const [isDeleteSupervisorOpen, setIsDeleteSupervisorOpen] = useState(false);

  // Obtener datos del owner desde localStorage
  const owner = getCurrentOwner();
  const currentShopId = getActiveShopId();

  // Si no hay owner, redirigir al login
  useEffect(() => {
    if (!owner) {
      toast({
        title: "Acceso no autorizado",
        description: "Debes iniciar sesión como dueño",
        variant: "destructive",
      });
      setLocation("/login");
    } else {
      // ============ LIMPIAR SESIÓN DE TIENDA AL ENTRAR COMO OWNER ============
      // Esto evita que el Navbar muestre la interfaz de tienda
      const currentShop = getCurrentShop();
      if (currentShop) {
        console.log(
          "🧹 Limpiando sesión de tienda al entrar al OwnerDashboard",
        );
        localStorage.removeItem("currentShop");
        localStorage.removeItem("currentUser");
        localStorage.removeItem("activeShopId");
      }
      // ============ FIN LIMPIAR SESIÓN ============
    }
  }, [owner, setLocation]);

  // Fetch datos frescos de las tiendas
  const {
    data: freshShops,
    refetch,
    isLoading,
    error: shopsError,
  } = useQuery({
    queryKey: ["owner-shops", owner?.id],
    queryFn: () => fetchOwnerShops(owner!.id),
    enabled: !!owner,
  });

  // Fetch estadísticas en tiempo real
  const { data: realtimeStats, error: statsError } = useQuery({
    queryKey: ["owner-realtime-stats", owner?.id],
    queryFn: () => getOwnerRealtimeStats(owner!.id),
    enabled: !!owner,
  });

  // Fetch supervisores
  const { data: supervisors = [], refetch: refetchSupervisors } = useQuery({
    queryKey: ["owner-supervisors", owner?.id],
    queryFn: () => getOwnerSupervisors(owner!.id),
    enabled: !!owner,
  });

  const queryClient = useQueryClient();

  const createSupervisorMutation = useMutation({
    mutationFn: () => createOwnerSupervisor(owner!.id, supervisorForm),
    onSuccess: () => {
      toast({ title: "Supervisor creado", description: "El supervisor se ha creado correctamente" });
      setIsCreateSupervisorOpen(false);
      setSupervisorForm({ shopId: "", username: "", password: "", fullName: "", email: "", phone: "" });
      queryClient.invalidateQueries({ queryKey: ["owner-supervisors"] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Error al crear supervisor", variant: "destructive" });
    },
  });

  const updateSupervisorMutation = useMutation({
    mutationFn: (args: { userId: string; data: typeof editSupervisorForm }) =>
      updateOwnerSupervisor(owner!.id, args.userId, args.data),
    onSuccess: () => {
      toast({ title: "Supervisor actualizado", description: "Los cambios se guardaron correctamente" });
      setIsEditSupervisorOpen(false);
      setEditingSupervisor(null);
      setEditSupervisorForm({ fullName: "", username: "", email: "", phone: "", password: "" });
      queryClient.invalidateQueries({ queryKey: ["owner-supervisors"] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Error al actualizar supervisor", variant: "destructive" });
    },
  });

  const deleteSupervisorMutation = useMutation({
    mutationFn: (userId: string) => deleteOwnerSupervisor(owner!.id, userId),
    onSuccess: () => {
      toast({ title: "Supervisor eliminado", description: "El supervisor se ha eliminado correctamente" });
      setIsDeleteSupervisorOpen(false);
      setDeletingSupervisor(null);
      queryClient.invalidateQueries({ queryKey: ["owner-supervisors"] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Error al eliminar supervisor", variant: "destructive" });
    },
  });

  // Obtener información del plan ← NUEVO HOOK AQUÍ
  const { planType, planLimits, planName, can } = usePlan();

  // Usar freshShops si está disponible, sino usar localStorage
  const shops = freshShops || getOwnerShops();

  // Actualizar shops si hay datos frescos
  useEffect(() => {
    if (freshShops) {
      // ✅ ACTUALIZAR localStorage con los datos frescos
      setOwnerShops(freshShops);
      setActiveShop(
        currentShopId || (freshShops.length > 0 ? freshShops[0].id : null),
      );
    }
  }, [freshShops, currentShopId]);

  // Función para cambiar de tienda
  const switchToShop = (shop: any) => {
    setActiveShopId(shop.id);
    setCurrentShop(shop);

    // Crear usuario temporal para el shop
    const tempUser = {
      id: `shop_${shop.id}`,
      shopId: shop.id,
      username: shop.username,
      fullName: shop.owner,
      role: "supervisor" as const,
    };
    setCurrentUser(tempUser);

    // Obtener el owner actual y guardarlo con la clave CORRECTA "currentOwner"
    const currentOwner = getCurrentOwner();
    if (currentOwner) {
      // Guardar en AMBAS claves para mantener compatibilidad
      localStorage.setItem("currentOwner", JSON.stringify(currentOwner)); // ← ESTA ES LA CLAVE QUE USA getCurrentOwner()
      localStorage.setItem("shopOwner", JSON.stringify(currentOwner)); // ← Para compatibilidad con código antiguo
      console.log("✅ Owner guardado en localStorage:", currentOwner.ownerName);
    } else {
      console.warn("⚠️ [OwnerDashboard] No hay owner logueado");
    }

    setLocation("/shop/dashboard");
  };

  // Función para crear nueva tienda
  const createNewShop = () => {
    setLocation("/owner/create-shop");
  };

  const handleLogout = () => {
    // Limpiar TODO en localStorage
    localStorage.removeItem("currentOwner");
    localStorage.removeItem("ownerShops");
    localStorage.removeItem("activeShopId");
    localStorage.removeItem("currentShop");
    localStorage.removeItem("currentUser");

    // Redirigir a login
    window.location.href = "/shop/login"; // Usar window.location para forzar recarga completa
  };

  // ✅ AGREGAR AQUÍ
  const handleRefresh = async () => {
    try {
      console.log("🔄 Actualizando datos...");

      // Refrescar las tiendas
      await refetch();

      // Refrescar los datos del owner desde la base de datos
      if (owner?.id) {
        // Mostrar toast de carga
        toast({
          title: "Actualizando",
          description: "Obteniendo datos actualizados...",
        });

        // Obtener datos actualizados del owner desde la API
        const response = await fetch(`/api/owners/${owner.id}`);

        if (response.ok) {
          const updatedOwner = await response.json();

          console.log("✅ Datos actualizados del owner:", updatedOwner);

          // Actualizar localStorage
          localStorage.setItem("currentOwner", JSON.stringify(updatedOwner));

          // Mostrar toast de éxito
          toast({
            title: "✅ Datos actualizados",
            description: `Plan: ${updatedOwner.planType === "basic" ? "Básico" : updatedOwner.planType === "professional" ? "Profesional" : "Enterprise"}`,
          });

          // Recargar la página para aplicar todos los cambios
          window.location.reload();
        } else {
          console.error("❌ Error al obtener owner:", response.status);
          toast({
            title: "Error",
            description: "No se pudieron actualizar los datos del owner",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Actualizado",
          description: "Lista de tiendas actualizada",
        });
      }
    } catch (error) {
      console.error("❌ Error en handleRefresh:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar",
        variant: "destructive",
      });
    }
  };

  // Función para abrir modal de edición
  const handleEditShop = (shop: any) => {
    setEditingShop(shop);
    setIsEditModalOpen(true);
  };

  // Función para refrescar después de editar/eliminar
  const handleShopUpdated = () => {
    refetch(); // Refrescar datos de la API
  };

  // Calcular estadísticas
  const calculateStats = () => {
    const totalShops = shops.length;

    // Usar el estado del owner para determinar tiendas activas/en prueba
    const isOwnerActive = owner?.accountStatus === "active";
    const isOwnerTrial = owner?.accountStatus === "trial";

    // Todas las tiendas heredan el estado del owner
    const activeShops = isOwnerActive ? totalShops : 0;
    const trialShops = isOwnerTrial ? totalShops : 0;

    // Usar datos reales de la API si están disponibles
    const totalSales = realtimeStats?.data?.summary?.totalSalesToday || 0;
    const totalRevenue = realtimeStats?.data?.summary?.totalRevenueToday || 0;

    return {
      totalShops,
      activeShops,
      trialShops,
      totalSales,
      totalRevenue,
    };
  };

  const stats = calculateStats();

  // ============ MANEJAR ERROR 403 (CUENTA SUSPENDIDA) ===========
  // ============ MANEJAR ERROR 403 (CUENTA SUSPENDIDA) ============
  useEffect(() => {
    // Función para verificar si un error es 403
    const isSuspensionError = (error: any): boolean => {
      if (!error) return false;

      // Verificar diferentes formatos de error
      return (
        error?.status === 403 ||
        error?.response?.status === 403 ||
        error?.message?.includes("403") ||
        error?.message?.includes("suspendida")
      );
    };

    // Verificar si hay error 403 en cualquiera de las queries
    const isSuspended =
      isSuspensionError(shopsError) || isSuspensionError(statsError);

    if (isSuspended) {
      console.log("🚫 Cuenta suspendida detectada - Limpiando sesión");

      // ============ LIMPIAR TODO EL LOCALSTORAGE ============
      // Eliminar datos del owner
      localStorage.removeItem("currentOwner");
      localStorage.removeItem("ownerShops");
      localStorage.removeItem("activeShopId");

      // Eliminar datos de la tienda (usuario temporal)
      localStorage.removeItem("currentShop");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("shopOwner");

      console.log("✅ localStorage limpiado");

      // Mostrar toast de error
      toast({
        title: "Cuenta suspendida",
        description: "Tu período de prueba ha expirado. Serás redirigido.",
        variant: "destructive",
        duration: 3000,
      });

      // Redirigir a la página de cuenta suspendida
      setTimeout(() => {
        window.location.href = "/account-suspended"; // Usar href para recarga completa
      }, 2000);
    }
  }, [shopsError, statsError]);

  if (!owner) {
    return null; // Se redirigirá por el useEffect
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Building className="h-6 w-6" />
                {owner.businessName}
              </h1>
              <p className="text-muted-foreground">
                Panel de control empresarial • Dueño: {owner.ownerName}
              </p>
              {owner.currency && (
                <p className="text-sm text-muted-foreground mt-1">
                  Moneda: {getCurrencyDisplayName(owner.currency)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg">
                <Crown className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">
                  Plan {planName}
                </span>
              </div>
              {planType !== "enterprise" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => setLocation("/owner/plans")}
                >
                  Mejorar Plan
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh} // ← Cambiar esto
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
                />
                Actualizar
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Estadísticas rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Tiendas Totales
                  </p>
                  <h3 className="text-2xl font-bold">{stats.totalShops}</h3>
                </div>
                <Store className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Tiendas Activas
                  </p>
                  <h3 className="text-2xl font-bold">{stats.activeShops}</h3>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    En Período de Prueba
                  </p>
                  <h3 className="text-2xl font-bold">{stats.trialShops}</h3>
                </div>
                <AlertCircle className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Ventas Hoy
                  </p>
                  <h3 className="text-2xl font-bold">
                    {formatCurrency(
                      stats.totalRevenue,
                      owner.currency || "CUP",
                    )}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats.totalSales} transacciones
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs principales */}
        <Tabs defaultValue="shops" className="space-y-6">
          <TabsList className="flex flex-nowrap overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [-webkit-overflow-scrolling:touch] px-1">
            <TabsTrigger
              value="shops"
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <Store className="h-4 w-4" />
              <span className="hidden sm:inline">Mis Tiendas</span>
              <span className="sm:hidden">Tiendas</span>
            </TabsTrigger>

            {/* Pestaña Dashboard - Solo para Enterprise */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={
                      planType !== "enterprise" ? "cursor-not-allowed" : ""
                    }
                  >
                    <TabsTrigger
                      value="dashboard"
                      className={`flex items-center gap-2 whitespace-nowrap ${
                        planType !== "enterprise"
                          ? "opacity-50 pointer-events-none"
                          : ""
                      }`}
                      disabled={planType !== "enterprise"}
                    >
                      <Activity className="h-4 w-4" />
                      <span className="hidden sm:inline">Dashboard</span>
                      <span className="sm:hidden">Dash</span>
                    </TabsTrigger>
                  </div>
                </TooltipTrigger>
                {planType !== "enterprise" && (
                  <TooltipContent className="bg-amber-100 border-amber-300 text-amber-800 max-w-[200px] text-center">
                    <p className="font-medium">🔒 Requiere Plan Enterprise</p>
                    <p className="text-xs mt-1">
                      El dashboard con KPIs y estadísticas avanzadas solo está
                      disponible en el plan Enterprise.
                    </p>
                    <Button
                      variant="link"
                      className="text-xs text-amber-900 underline p-0 h-auto mt-2"
                      onClick={() => setLocation("/owner/plans")}
                    >
                      Mejorar plan
                    </Button>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            {/* Pestaña Reportes - Solo para Enterprise */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={
                      planType !== "enterprise" ? "cursor-not-allowed" : ""
                    }
                  >
                    <TabsTrigger
                      value="reports"
                      className={`flex items-center gap-2 whitespace-nowrap ${
                        planType !== "enterprise"
                          ? "opacity-50 pointer-events-none"
                          : ""
                      }`}
                      disabled={planType !== "enterprise"}
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span className="hidden sm:inline">Reportes</span>
                      <span className="sm:hidden">Rep.</span>
                    </TabsTrigger>
                  </div>
                </TooltipTrigger>
                {planType !== "enterprise" && (
                  <TooltipContent className="bg-amber-100 border-amber-300 text-amber-800 max-w-[200px] text-center">
                    <p className="font-medium">🔒 Requiere Plan Enterprise</p>
                    <p className="text-xs mt-1">
                      Los reportes consolidados multi-tienda están disponibles
                      solo en el plan Enterprise.
                    </p>
                    <Button
                      variant="link"
                      className="text-xs text-amber-900 underline p-0 h-auto mt-2"
                      onClick={() => setLocation("/owner/plans")}
                    >
                      Mejorar plan
                    </Button>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <TabsTrigger
              value="settings"
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Configuración</span>
              <span className="sm:hidden">Conf.</span>
            </TabsTrigger>
            <TabsTrigger
              value="supervisors"
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Supervisores</span>
              <span className="sm:hidden">Sup.</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab: Mis Tiendas */}
          <TabsContent value="shops" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Tus Puntos de Venta</h2>
              <div className="relative group">
                <Button
                  onClick={createNewShop}
                  disabled={!can.canCreateShop(shops.length)}
                  className={
                    !can.canCreateShop(shops.length)
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Añadir Nueva Tienda
                </Button>
                {!can.canCreateShop(shops.length) && (
                  <div className="absolute right-0 mt-2 w-64 p-2 bg-amber-100 border border-amber-300 rounded-md text-xs text-amber-800 shadow-lg z-50 hidden group-hover:block">
                    <p className="font-medium">⚠️ Límite alcanzado</p>
                    <p>
                      Has alcanzado el máximo de{" "}
                      {planLimits.maxShops === Infinity
                        ? "ilimitadas"
                        : planLimits.maxShops}{" "}
                      tiendas para tu plan {planName}.
                    </p>
                    <p className="mt-1 text-amber-700">
                      Mejora tu plan para crear más tiendas.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {shops.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    No hay tiendas registradas
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Comienza creando tu primera tienda para gestionar tu negocio
                  </p>
                  <Button onClick={createNewShop}>Crear Primera Tienda</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {shops.map((shop) => (
                  <Card
                    key={shop.id}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg">{shop.name}</CardTitle>
                        <Badge
                          variant={
                            owner.accountStatus === "active"
                              ? "default"
                              : owner.accountStatus === "trial"
                                ? "secondary"
                                : "destructive"
                          }
                        >
                          {owner.accountStatus === "active"
                            ? "Activa"
                            : owner.accountStatus === "trial"
                              ? "Prueba"
                              : "Suspendida"}
                        </Badge>
                      </div>
                      <CardDescription>{shop.address}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Usuario:
                          </span>
                          <span className="font-medium">{shop.username}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Encargado:
                          </span>
                          <span className="font-medium">{shop.owner}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Creada:</span>
                          <span className="font-medium">
                            {new Date(shop.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-4">
                        <Button
                          variant="default"
                          className="flex-1"
                          onClick={() => switchToShop(shop)}
                        >
                          <ArrowRight className="h-4 w-4 mr-2" />
                          Entrar
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleEditShop(shop)}
                        >
                          Editar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Card para añadir nueva tienda */}
                {can.canCreateShop(shops.length) ? (
                  <Card className="border-dashed border-2 hover:border-primary transition-colors">
                    <CardContent className="pt-6 flex flex-col items-center justify-center min-h-[200px] text-center">
                      <div className="rounded-full bg-primary/10 p-3 mb-4">
                        <Plus className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="font-medium text-lg mb-2">
                        Añadir Nueva Tienda
                      </h3>
                      <p className="text-muted-foreground text-sm mb-2">
                        {shops.length} de{" "}
                        {planLimits.maxShops === Infinity
                          ? "∞"
                          : planLimits.maxShops}{" "}
                        tiendas
                      </p>
                      <p className="text-muted-foreground text-sm mb-6">
                        Expande tu negocio con otro punto de venta
                      </p>
                      <Button
                        onClick={createNewShop}
                        disabled={!can.canCreateShop(shops.length)}
                      >
                        Crear Tienda
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-2 border-amber-200 bg-amber-50">
                    <CardContent className="pt-6 flex flex-col items-center justify-center min-h-[200px] text-center">
                      <div className="rounded-full bg-amber-100 p-3 mb-4">
                        <AlertCircle className="h-8 w-8 text-amber-600" />
                      </div>
                      <h3 className="font-medium text-lg mb-2">
                        Límite Alcanzado
                      </h3>
                      <p className="text-muted-foreground text-sm mb-2">
                        Has alcanzado el máximo de{" "}
                        {planLimits.maxShops === Infinity
                          ? "ilimitadas"
                          : planLimits.maxShops}{" "}
                        tiendas
                      </p>
                      <p className="text-muted-foreground text-sm mb-6">
                        Actualiza tu plan para crear más tiendas
                      </p>
                      <Button
                        variant="default"
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => setLocation("/owner/plans")}
                      >
                        Mejorar Plan
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Tab: Dashboard KPI - Solo Enterprise */}
          <TabsContent value="dashboard">
            {planType === "enterprise" ? (
              owner ? (
                <OwnerDashboardKPI />
              ) : (
                <Card>
                  <CardContent className="pt-6 text-center py-12">
                    <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Inicia sesión como dueño para ver el dashboard
                    </p>
                  </CardContent>
                </Card>
              )
            ) : (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <Activity className="h-12 w-12 mx-auto text-amber-200 mb-4" />
                  <h3 className="text-xl font-medium text-amber-800 mb-2">
                    Dashboard Bloqueado
                  </h3>
                  <p className="text-amber-700 mb-6 max-w-md mx-auto">
                    El dashboard con KPIs y estadísticas avanzadas está
                    disponible solo en el plan Enterprise.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setLocation("/owner/plans")}
                    className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                  >
                    Mejorar a Enterprise
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Reportes Consolidados - Solo Enterprise */}
          <TabsContent value="reports">
            {planType === "enterprise" ? (
              owner ? (
                <OwnerReports />
              ) : (
                <Card>
                  <CardContent className="pt-6 text-center py-12">
                    <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Inicia sesión como dueño para ver los reportes
                    </p>
                  </CardContent>
                </Card>
              )
            ) : (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <BarChart3 className="h-12 w-12 mx-auto text-amber-200 mb-4" />
                  <h3 className="text-xl font-medium text-amber-800 mb-2">
                    Reportes Bloqueados
                  </h3>
                  <p className="text-amber-700 mb-6 max-w-md mx-auto">
                    Los reportes consolidados multi-tienda están disponibles
                    solo en el plan Enterprise.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setLocation("/owner/plans")}
                    className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                  >
                    Mejorar a Enterprise
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Configuración */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Configuración de Cuenta</CardTitle>
                <CardDescription>
                  Actualiza tu información personal y cambia tu contraseña
                </CardDescription>
              </CardHeader>
              <CardContent>
                {owner ? (
                  <OwnerAccountSettings owner={owner} />
                ) : (
                  <div className="text-center py-12">
                    <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Debes iniciar sesión como dueño para configurar tu cuenta
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Supervisores */}
          <TabsContent value="supervisors" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Supervisores</h2>
              <Dialog open={isCreateSupervisorOpen} onOpenChange={setIsCreateSupervisorOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Supervisor
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Nuevo Supervisor</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label>Nombre completo</Label>
                      <Input
                        value={supervisorForm.fullName}
                        onChange={(e) => setSupervisorForm({ ...supervisorForm, fullName: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Nombre de usuario</Label>
                      <Input
                        value={supervisorForm.username}
                        onChange={(e) => setSupervisorForm({ ...supervisorForm, username: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Contraseña</Label>
                      <Input
                        type="password"
                        value={supervisorForm.password}
                        onChange={(e) => setSupervisorForm({ ...supervisorForm, password: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={supervisorForm.email}
                        onChange={(e) => setSupervisorForm({ ...supervisorForm, email: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Teléfono</Label>
                      <Input
                        value={supervisorForm.phone}
                        onChange={(e) => setSupervisorForm({ ...supervisorForm, phone: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Tienda de origen</Label>
                      <Select
                        value={supervisorForm.shopId}
                        onValueChange={(v) => setSupervisorForm({ ...supervisorForm, shopId: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar tienda" />
                        </SelectTrigger>
                        <SelectContent>
                          {shops.map((shop: any) => (
                            <SelectItem key={shop.id} value={shop.id}>
                              {shop.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsCreateSupervisorOpen(false)}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={() => createSupervisorMutation.mutate()}
                      disabled={!supervisorForm.shopId || !supervisorForm.username || !supervisorForm.password || !supervisorForm.fullName || createSupervisorMutation.isPending}
                    >
                      {createSupervisorMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Crear
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>Supervisores</CardTitle>
                <CardDescription>
                  Usuarios con acceso a todas las tiendas de tu negocio
                </CardDescription>
              </CardHeader>
              <CardContent>
                {supervisors.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      No hay supervisores registrados
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Crea un supervisor usando el botón "Crear Supervisor"
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {supervisors.map((sup: any) => (
                      <div
                        key={sup.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{sup.fullName || sup.username}</p>
                          <p className="text-sm text-muted-foreground">{sup.username}</p>
                          {sup.email && (
                            <p className="text-sm text-muted-foreground">{sup.email}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">Supervisor</Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingSupervisor(sup);
                              setEditSupervisorForm({
                                fullName: sup.fullName || "",
                                username: sup.username || "",
                                email: sup.email || "",
                                phone: sup.phone || "",
                                password: "",
                              });
                              setIsEditSupervisorOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setDeletingSupervisor(sup);
                              setIsDeleteSupervisorOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Modal de edición de supervisor */}
      <Dialog open={isEditSupervisorOpen} onOpenChange={setIsEditSupervisorOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Supervisor</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nombre completo</Label>
              <Input
                value={editSupervisorForm.fullName}
                onChange={(e) => setEditSupervisorForm({ ...editSupervisorForm, fullName: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Nombre de usuario</Label>
              <Input
                value={editSupervisorForm.username}
                onChange={(e) => setEditSupervisorForm({ ...editSupervisorForm, username: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editSupervisorForm.email}
                onChange={(e) => setEditSupervisorForm({ ...editSupervisorForm, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Teléfono</Label>
              <Input
                value={editSupervisorForm.phone}
                onChange={(e) => setEditSupervisorForm({ ...editSupervisorForm, phone: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label>Nueva contraseña (dejar vacío para mantener)</Label>
              <Input
                type="password"
                value={editSupervisorForm.password}
                onChange={(e) => setEditSupervisorForm({ ...editSupervisorForm, password: e.target.value })}
                placeholder="Dejar vacío para mantener la actual"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditSupervisorOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                const updates: any = {};
                if (editSupervisorForm.fullName !== editingSupervisor?.fullName) updates.fullName = editSupervisorForm.fullName;
                if (editSupervisorForm.username !== editingSupervisor?.username) updates.username = editSupervisorForm.username;
                if (editSupervisorForm.email !== editingSupervisor?.email) updates.email = editSupervisorForm.email;
                if (editSupervisorForm.phone !== editingSupervisor?.phone) updates.phone = editSupervisorForm.phone;
                if (editSupervisorForm.password) updates.password = editSupervisorForm.password;
                if (Object.keys(updates).length === 0) {
                  toast({ title: "Sin cambios", description: "No hay cambios para guardar" });
                  return;
                }
                updateSupervisorMutation.mutate({ userId: editingSupervisor.id, data: updates });
              }}
              disabled={updateSupervisorMutation.isPending}
            >
              {updateSupervisorMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmación de eliminación */}
      <Dialog open={isDeleteSupervisorOpen} onOpenChange={setIsDeleteSupervisorOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar Supervisor</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>¿Estás seguro de eliminar a <strong>{deletingSupervisor?.fullName || deletingSupervisor?.username}</strong>?</p>
            <p className="text-sm text-muted-foreground mt-2">
              Esta acción no se puede deshacer. El usuario perderá acceso al sistema.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteSupervisorOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteSupervisorMutation.mutate(deletingSupervisor.id)}
              disabled={deleteSupervisorMutation.isPending}
            >
              {deleteSupervisorMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de edición/eliminación tiendas */}
      {owner && editingShop && (
        <ShopEditModal
          shop={editingShop}
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          onShopUpdated={handleShopUpdated}
          ownerId={owner.id}
        />
      )}
    </div>
  );
}
